import React, { useState } from "react";
import {
  Box, Card, CardContent, Container, Typography, TextField, Button,
  Stack, Divider, Chip, Alert,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { db } from "../database/db";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useSelector } from "react-redux";
import Page from "../components/Page";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function MassNotification() {
  const admin = useSelector((state) => state.adminData?.data);
  const [title, setTitle] = useState("Fire Investing");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Message cannot be empty");
      return;
    }
    setLoading(true);
    try {
      const functions = getFunctions();
      const sendMassNotification = httpsCallable(functions, "sendMassNotification");
      await sendMassNotification({ title, message });

      setHistory((prev) => [
        { title, message, sentAt: new Date().toLocaleString() },
        ...prev.slice(0, 4),
      ]);
      toast.success("Notification sent to all users!");
      setMessage("");
    } catch (err) {
      toast.error("Failed to send: " + err.message);
    }
    setLoading(false);
  };

  return (
    <Page title="Mass Notification | Fire Investing Admin">
      <ToastContainer position="top-right" />
      <Container maxWidth="md">
        <Box sx={{ pb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Mass Notification</Typography>
          <Typography variant="body2" color="text.secondary">
            Send a push notification to every Fire Investing user
          </Typography>
        </Box>
        <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)", mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              This will send a push notification to <strong>all active users</strong>. Use responsibly.
            </Alert>
            <Stack spacing={2.5} component="form" onSubmit={handleSend}>
              <TextField
                fullWidth
                label="Notification Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                InputProps={{
                  startAdornment: <Icon icon="eva:bell-fill" width={20} style={{ marginRight: 8, color: "#FF6C40" }} />,
                }}
              />
              <TextField
                fullWidth
                multiline
                rows={5}
                label="Message"
                placeholder="Enter your message to the community..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                helperText={`${message.length} characters`}
              />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Chip
                  icon={<Icon icon="eva:people-fill" width={16} />}
                  label="Sending to: All Users"
                  color="primary"
                  variant="outlined"
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading || !message.trim()}
                  startIcon={<Icon icon="eva:bell-fill" />}
                  sx={{ bgcolor: "#FF6C40", "&:hover": { bgcolor: "#E05A30" }, px: 4 }}
                >
                  {loading ? "Sending..." : "Send Notification"}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {history.length > 0 && (
          <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Recent Notifications (This Session)</Typography>
              <Divider sx={{ mb: 2 }} />
              {history.map((h, i) => (
                <Box key={i} sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{h.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{h.sentAt}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">{h.message}</Typography>
                  {i < history.length - 1 && <Divider sx={{ mt: 1.5 }} />}
                </Box>
              ))}
            </CardContent>
          </Card>
        )}
      </Container>
    </Page>
  );
}
